import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import type { NavigationNode, SchedulerStatus } from '@repo/types';
import { useSchedulerStatusQuery } from '@data/cms/useSchedulerStatusQuery';
import { isComponentVisible } from '../../../utils/schedulerVisibility';

export type ActiveNavigationChild = Omit<NavigationNode, 'children'> & { isActive: boolean };
export type ActiveNavigationNode = Omit<NavigationNode, 'children'> & {
  isActive: boolean;
  children?: ActiveNavigationChild[];
};

/**
 * Recursively filters nodes where visible === false or the node's scheduler evaluates
 * inactive, dropping the entire subtree when a parent is hidden either way. Nodes without
 * a visible field default to visible; nodes without a scheduler default to always active.
 */
export function filterVisibleNodes(
  nodes: NavigationNode[],
  schedulerStatus: SchedulerStatus | null = null
): NavigationNode[] {
  return nodes
    .filter((node) => node.visible !== false && isComponentVisible(node.scheduler, undefined, schedulerStatus))
    .map((node) => ({
      ...node,
      children: node.children ? filterVisibleNodes(node.children, schedulerStatus) : node.children,
    }));
}

// Any scheme other than http(s) (javascript:, data:, vbscript:, …) is unsafe
// to render into <a href>. Written as a single negative lookahead so optional
// leading whitespace cannot backtrack around the check.
// eslint-disable-next-line sonarjs/regular-expr -- anchored, sequential quantifiers only, no backtracking risk
const SAFE_HREF_PATTERN = /^(?!\s*(?!https?:)[a-z][a-z0-9+.-]*:)/i;

/**
 * Prefixes bare domains ("example.com/x") with https:// so they leave the SPA
 * router, and neuters dangerous schemes: nav URLs are editor-supplied content.
 */
export function toHref(url: string): string {
  const href = url.includes('.') && !url.startsWith('http') ? `https://${url}` : url;
  return SAFE_HREF_PATTERN.test(href) ? href : '#';
}

/** target/rel pair for editor-supplied links; new tabs are opened without an opener handle. */
export function linkTargetProps(sameTab: boolean): { target: string; rel?: string } {
  return sameTab ? { target: '_self' } : { target: '_blank', rel: 'noopener noreferrer' };
}

type ParsedUrl = {
  path: string;
  query?: string;
  hash?: string;
};

// Helper function to parse URL into components
const parseUrl = (url: string): ParsedUrl => {
  // Handle URLs that are just a hash (e.g., #midlands)
  if (url.startsWith('#')) {
    return { path: '', hash: url };
  }

  const [pathWithQuery, hash] = url.split('#');
  const [path, query] = pathWithQuery!.split('?');
  // Ensure hash includes the # prefix if it exists
  return { path: path!, query, hash: hash ? `#${hash}` : undefined };
};

// Check if URL is external
const isExternalUrl = (url: string): boolean => {
  return url.startsWith('http') || (url.includes('.') && !url.startsWith('/'));
};

// Normalize path by ensuring it starts with /
const normalizePath = (path: string): string => {
  if (!path) return '';
  return path.startsWith('/') ? path : `/${path}`;
};

// Check if two URLs match based on their components
const urlsMatch = (
  navUrl: ParsedUrl,
  currentUrl: ParsedUrl,
  navUrlString: string,
  currentUrlString: string
): boolean => {
  // Normalize paths for comparison
  const normalizedNavPath = normalizePath(navUrl.path);
  const normalizedCurrentPath = normalizePath(currentUrl.path);

  // Exact match (full string comparison) - also try with normalized paths
  if (navUrlString === currentUrlString) {
    return true;
  }

  // Match with hash (e.g., /club-overview#premier)
  // Hashes are already normalized with # prefix in parseUrl
  if (navUrl.hash && currentUrl.hash) {
    // If nav item is just a hash (e.g., #midlands), match any path with that hash
    if (!navUrl.path && navUrl.hash === currentUrl.hash) {
      return true;
    }
    // If both have paths, they must match along with the hash
    if (normalizedNavPath === normalizedCurrentPath && navUrl.hash === currentUrl.hash) {
      return true;
    }
  }

  // Also check path match without hash if nav doesn't have hash but current does
  return Boolean(!navUrl.hash && !navUrl.query && currentUrl.path && normalizedNavPath === normalizedCurrentPath);
};

// Check if a plain path should be active (fallback when no exact match exists)
const isPathActiveAsFallback = (
  navUrl: ParsedUrl,
  _currentUrl: ParsedUrl,
  currentPath: string,
  hasExactMatch: boolean
): boolean => {
  // Only match plain paths when nav item has no query/hash and no exact match exists
  if (!navUrl.query && !navUrl.hash && !hasExactMatch) {
    const normalizedNavPath = normalizePath(navUrl.path);
    const normalizedCurrentPath = normalizePath(currentPath);

    // Nested route match (exact path match is handled by urlsMatch)
    if (normalizedNavPath !== '/' && normalizedCurrentPath.startsWith(normalizedNavPath + '/')) {
      return true;
    }
  }
  return false;
};

const matchesQuery = (itemParsed: ParsedUrl, currentParsed: ParsedUrl): boolean =>
  Boolean(itemParsed.query && currentParsed.query && itemParsed.query === currentParsed.query);

const matchesHash = (itemParsed: ParsedUrl, currentParsed: ParsedUrl): boolean =>
  Boolean(itemParsed.hash && currentParsed.hash && itemParsed.hash === currentParsed.hash);

const isPlainUrl = (parsed: ParsedUrl): boolean => !parsed.query && !parsed.hash;

const isNestedPath = (itemParsed: ParsedUrl, currentParsed: ParsedUrl): boolean => {
  const normalizedItemPath = normalizePath(itemParsed.path);
  return normalizedItemPath !== '/' && normalizePath(currentParsed.path).startsWith(normalizedItemPath + '/');
};

/**
 * Scores how well an item's own URL matches the current location:
 * 90 exact full match, 80 path+query, 70 path+hash, 50 path only,
 * 30 nested route, 0 no match. (Child matches score 100, handled by caller.)
 */
function scoreOwnUrl(url: string, currentParsed: ParsedUrl, currentFullPath: string): number {
  if (url === currentFullPath) {
    return 90;
  }
  const itemParsed = parseUrl(url);
  const samePath = normalizePath(itemParsed.path) === normalizePath(currentParsed.path);
  if (samePath && matchesQuery(itemParsed, currentParsed)) {
    return 80;
  }
  if (samePath && matchesHash(itemParsed, currentParsed)) {
    return 70;
  }
  if (isPlainUrl(itemParsed) && samePath) {
    return 50;
  }
  if (isPlainUrl(itemParsed) && isNestedPath(itemParsed, currentParsed)) {
    return 30;
  }
  return 0;
}

/** Collects the internal (non-external) URLs of all items and their children. */
function collectInternalUrls(nodes: NavigationNode[]): string[] {
  const urls: string[] = [];
  nodes.forEach((item) => {
    if (item.url && !isExternalUrl(item.url)) {
      urls.push(item.url);
    }
    item.children?.forEach((child) => {
      if (child.url && !isExternalUrl(child.url)) {
        urls.push(child.url);
      }
    });
  });
  return urls;
}

type ScoreContext = {
  currentParsed: ParsedUrl;
  currentFullPath: string;
  checkItemActive: (url: string | null | undefined) => boolean;
};

type ScoredItem = {
  item: NavigationNode;
  score: number;
  activeChild: NavigationNode | null;
};

/** Scores one root item: an active child wins outright (100), else the item's own URL. */
function scoreNavItem(item: NavigationNode, ctx: ScoreContext): ScoredItem {
  const activeChild = (item.children ?? []).find((child) => ctx.checkItemActive(child.url)) ?? null;
  if (activeChild) {
    return { item, score: 100, activeChild };
  }
  if (item.url && !isExternalUrl(item.url)) {
    return { item, score: scoreOwnUrl(item.url, ctx.currentParsed, ctx.currentFullPath), activeChild: null };
  }
  return { item, score: 0, activeChild: null };
}

/** Pure core of useActiveNav: annotates visible nodes with active state for a location. */
function computeActiveNodes(
  visibleNodes: NavigationNode[],
  currentFullPath: string,
  pathname: string
): ActiveNavigationNode[] {
  const currentParsed = parseUrl(currentFullPath);

  // Check if there's an exact match anywhere in the navigation
  const hasExactMatch = collectInternalUrls(visibleNodes).some((url) =>
    urlsMatch(parseUrl(url), currentParsed, url, currentFullPath)
  );

  const checkItemActive = (url: string | null | undefined): boolean => {
    if (!url || isExternalUrl(url)) return false;
    const itemParsed = parseUrl(url);
    if (urlsMatch(itemParsed, currentParsed, url, currentFullPath)) {
      return true;
    }
    return isPathActiveAsFallback(itemParsed, currentParsed, pathname, hasExactMatch);
  };

  const itemsWithScores = visibleNodes.map((item) =>
    scoreNavItem(item, { currentParsed, currentFullPath, checkItemActive })
  );

  // Find the single best matching root item. The accumulator's item is
  // honestly nullable: the seed has no item until a positive score wins.
  const bestRootMatch = itemsWithScores.reduce<{ item: NavigationNode | null; score: number }>(
    (best, current) => (current.score > best.score ? current : best),
    { item: null, score: 0 }
  );

  return visibleNodes.map((item) => {
    const scoreItem = itemsWithScores.find((si) => si.item === item);
    const isTheBestMatch = bestRootMatch.item === item && bestRootMatch.score > 0;

    return {
      ...item,
      isActive: isTheBestMatch,
      children: item.children?.map((child) => ({
        ...child,
        isActive: isTheBestMatch && scoreItem?.activeChild === child,
      })),
    };
  });
}

/**
 * Annotates navigation nodes with active state derived from the current
 * location: URL parsing, exact/query/hash scoring and nested-route fallback.
 * Extracted from the old Nav.tsx so behavior is unchanged.
 */
export function useActiveNav(nodes: NavigationNode[] | undefined): ActiveNavigationNode[] {
  const location = useLocation();

  // Track hash changes manually since React Router doesn't always update location.hash
  const [currentHash, setCurrentHash] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''));

  // Hashes injected by ad networks (Google Vignette, etc.) that should not trigger re-renders
  const isAdHash = useCallback((hash: string) => hash === '#google_vignette' || hash.startsWith('#goog_'), []);

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (!isAdHash(hash)) {
        setCurrentHash(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    // Also update when location changes (skip ad hashes)
    const hash = window.location.hash;
    if (!isAdHash(hash)) {
      setCurrentHash(hash);
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location, isAdHash]);

  const { data: schedulerStatus } = useSchedulerStatusQuery();

  const visibleNodes = useMemo(
    () => (nodes ? filterVisibleNodes(nodes, schedulerStatus ?? null) : []),
    [nodes, schedulerStatus]
  );

  // Memoize the annotated items to prevent unnecessary re-renders
  return useMemo(() => {
    if (visibleNodes.length === 0) {
      return [];
    }
    // Current location - use tracked hash instead of location.hash
    const currentFullPath = location.pathname + location.search + currentHash;
    return computeActiveNodes(visibleNodes, currentFullPath, location.pathname);
  }, [visibleNodes, location.pathname, location.search, currentHash]);
}
